import React, { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { DASHBOARD_ROUTE, REPORT_REM_ROUTE, REPORT_ROUTE } from "../config";
import { EtapaRelatorio, ReportJob, ReportJobStatus, waitReportJob } from "../features/dashboard/api/waitReportJob";
import { useNotificationStore } from "../stores/notifications";

/** Um total do resumo do relatorio, ja formatado para exibicao. */
export interface IResumoItem {
  label: string;
  valor: string;
  /**
   * Valor original quando o total mostrado foi ajustado (modo "Projecao
   * editavel"). Aparece pequeno ao lado, como no canto das celulas da tabela.
   */
  original?: string;
}

/** O que o cabecalho mostra no lugar do titulo quando o resumo se recolhe. */
export interface IResumoHeader {
  totais: IResumoItem[];
  /** Conteudo do dropdown de graficos, que sobe junto com os totais. */
  graficos: ReactNode;
  /** Quantos filtros estao na tela: os totais ja vem filtrados. */
  filtros?: number;
}

/** Estado do relatorio sendo gerado, como a tela de progresso o desenha. */
export interface IJobEmAndamento {
  id: string;
  etapas: EtapaRelatorio[];
  /** ReportTypes do backend. Vem do job, nao da tela: sobrevive a um F5. */
  tipo?: string;
  status?: ReportJobStatus;
  /** Origem do cronometro (`startedAt`, ou `createdAt` enquanto a task sobe). */
  iniciadoEm: number | null;
  /** Acompanhamento retomado (F5, outra aba, outro computador). */
  retomado: boolean;
  /** Calculo terminou; a pagina do relatorio ainda vai buscar e montar a arvore. */
  concluido: boolean;
}

/** Relatorio que terminou enquanto a pessoa estava em outra tela. */
export interface IRelatorioPronto {
  id: string;
  tipo?: string;
}

interface ReportContextType {
  isGeneratingReport: boolean;
  setIsGeneratingReport: (value: boolean) => void;
  /**
   * Totais que o cabecalho deve mostrar no lugar do titulo. O card de resumo
   * publica aqui quando rola para fora da tela, e limpa ao sair da pagina - o
   * cabecalho vive no Layout, fora da arvore do relatorio, entao o contexto e o
   * unico caminho entre os dois.
   */
  resumoHeader: IResumoHeader | null;
  setResumoHeader: (value: IResumoHeader | null) => void;
  /**
   * Conteudo livre que uma pagina sobe para o cabecalho no lugar do titulo (o Dashboard
   * poe ali os botoes de gerar relatorio quando o card deles rola para fora da tela).
   */
  acoesHeader: ReactNode | null;
  setAcoesHeader: (value: ReactNode | null) => void;

  job: IJobEmAndamento | null;
  /** Comeca (ou reaproveita) o acompanhamento de um job. Nunca rejeita: a falha vira notificacao. */
  acompanharJob: (jobId: string, retomado: boolean) => Promise<void>;
  relatorioPronto: IRelatorioPronto | null;
  fecharRelatorioPronto: () => void;
}

const ReportContext = createContext<ReportContextType | undefined>(undefined);

// A pagina depende do tipo: a remarcacao tem arvore e colunas proprias. Headers gravados
// antes de a remarcacao ser salva nao tem `type`, e caem no estoque ideal.
export const rotaDoRelatorio = (tipo?: string) => tipo === "Rem" ? REPORT_REM_ROUTE : REPORT_ROUTE;

/**
 * O acompanhamento do relatorio mora aqui, e nao na tela do Dashboard.
 *
 * Quando morava la, sair do Dashboard deixava a consulta rodando solta e, ao
 * terminar, ela abria o relatorio de onde quer que a pessoa estivesse - no meio
 * de outra tarefa. Aqui ela sobrevive a troca de tela e decide ao terminar: quem
 * esta olhando o progresso no Dashboard vai direto para o relatorio; quem esta
 * em outra tela recebe o aviso de relatorio pronto e escolhe quando abrir.
 */
export const ReportProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [resumoHeader, setResumoHeader] = useState<IResumoHeader | null>(null);
  const [acoesHeader, setAcoesHeader] = useState<ReactNode | null>(null);
  const [job, setJob] = useState<IJobEmAndamento | null>(null);
  const [relatorioPronto, setRelatorioPronto] = useState<IRelatorioPronto | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { addNotification } = useNotificationStore();

  // Lida na hora em que o job termina, e nao no momento em que a consulta comecou.
  const rotaAtualRef = useRef(location.pathname);
  useEffect(() => { rotaAtualRef.current = location.pathname; }, [location.pathname]);

  // Um acompanhamento por job: voltar ao Dashboard no meio da geracao nao abre uma segunda consulta.
  const emCursoRef = useRef<{ id: string; promessa: Promise<void> } | null>(null);

  const acompanharJob = useCallback((jobId: string, retomado: boolean) => {
    if (emCursoRef.current?.id === jobId) return emCursoRef.current.promessa;

    setIsGeneratingReport(true);
    setJob({ id: jobId, etapas: [], iniciadoEm: null, retomado, concluido: false });

    const promessa = (async () => {
      try {
        const final = await waitReportJob(jobId, (parcial: ReportJob) => {
          // O `createdAt` cobre o vao em que o `startedAt` ainda e nulo (task subindo) e,
          // por vir do servidor, sobrevive a um F5 - um `Date.now()` local reiniciaria a contagem.
          const origem = parcial.startedAt ?? parcial.createdAt;

          setJob(atual => atual && atual.id === jobId ? {
            ...atual,
            etapas: parcial.etapas ?? [],
            tipo: parcial.type,
            status: parcial.status,
            concluido: parcial.status === 'done',
            iniciadoEm: origem ? new Date(origem).getTime() : atual.iniciadoEm,
          } : atual);
        });

        if (rotaAtualRef.current.startsWith(DASHBOARD_ROUTE)) {
          navigate(`${rotaDoRelatorio(final.type)}?r=${final.report}`);
        } else if (final.report) {
          setRelatorioPronto({ id: final.report, tipo: final.type });
        }
      } catch (error: any) {
        // `error.message` carrega a causa vinda do job (um estouro de memoria, por exemplo).
        addNotification({ type: "error", title: error?.response?.data?.message || error?.message || 'Erro ao gerar o relatório' });
      } finally {
        emCursoRef.current = null;
        setJob(null);
        setIsGeneratingReport(false);
      }
    })();

    emCursoRef.current = { id: jobId, promessa };
    return promessa;
  }, [navigate, addNotification]);

  const fecharRelatorioPronto = useCallback(() => setRelatorioPronto(null), []);

  return (
    <ReportContext.Provider value={{
      isGeneratingReport, setIsGeneratingReport, resumoHeader, setResumoHeader, acoesHeader, setAcoesHeader,
      job, acompanharJob, relatorioPronto, fecharRelatorioPronto,
    }}>
      {children}
    </ReportContext.Provider>
  );
};

export const useReportContext = () => {
  const context = useContext(ReportContext);
  if (!context) throw new Error("useReportContext must be used within ReportProvider");
  return context;
};
